import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import DashboardPage from "@/pages/app/page";
import ProjectPage from "@/pages/app/projects/[id]/page";
import ProjectsPage from "@/pages/app/projects/page";
import StaffTodayPage from "@/modules/project/ui/staff-today-page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

interface ProjectsSearch {
  status?: string;
  who?: string;
  view?: string;
  due?: string;
  from?: string;
  month?: string;
  day?: string;
  q?: string;
  page?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const validateProjectsSearch = (search: Record<string, unknown>): ProjectsSearch => ({
  status: textOf(search.status),
  who: textOf(search.who),
  view: textOf(search.view),
  due: textOf(search.due),
  from: textOf(search.from),
  month: textOf(search.month),
  day: textOf(search.day),
  q: textOf(search.q),
  page: textOf(search.page),
});

const dashboardQuery = () => viewQuery("dashboard", {});
const projectsQuery = (search: ProjectsSearch) => viewQuery("projects", search);
const projectQuery = (id: string) => viewQuery("project", { id });
const staffTodayQuery = () => viewQuery("staff-today", {});

const DashboardRouteComponent = () => {
  const { data } = useSuspenseQuery(dashboardQuery());
  return <DashboardPage data={data} />;
};

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  loader: ({ context }) => loadView(context.queryClient, dashboardQuery()),
  head: () => brandHead("대시보드"),
  component: DashboardRouteComponent,
  ...APP_BOUNDARIES,
});

const ProjectsRouteComponent = () => {
  const search = projectsRoute.useSearch();
  const { data } = useSuspenseQuery(projectsQuery(search));
  return <ProjectsPage data={data} />;
};

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects",
  validateSearch: validateProjectsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, projectsQuery(deps)),
  head: () => brandHead("현장"),
  component: ProjectsRouteComponent,
  ...APP_BOUNDARIES,
});

const ProjectRouteComponent = () => {
  const { id } = projectRoute.useParams();
  const { tab } = projectRoute.useSearch();
  const { data } = useSuspenseQuery(projectQuery(id));
  return <ProjectPage data={data} tab={tab} />;
};

const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id",
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({ tab: textOf(search.tab) }),
  loader: ({ context, params }) => loadView(context.queryClient, projectQuery(params.id)),
  // 제목은 읽어 둔 현장 이름 — 없으면 「현장」.
  head: ({ match, params }) => brandHead(match.context.queryClient.getQueryData(projectQuery(params.id).queryKey)?.title ?? "현장"),
  component: ProjectRouteComponent,
  ...APP_BOUNDARIES,
});

const StaffTodayRouteComponent = () => {
  const { data } = useSuspenseQuery(staffTodayQuery());
  return <StaffTodayPage data={data} />;
};

// 직원 앱 첫 화면 — 웹뷰가 연다.
const staffTodayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/today",
  loader: ({ context }) => loadView(context.queryClient, staffTodayQuery()),
  head: () => brandHead("오늘"),
  component: StaffTodayRouteComponent,
  ...APP_BOUNDARIES,
});

// g3a 배치의 라우트 — getParentRoute 가 가리키는 부모 아래로 root.tsx 가 꽂는다.
export const routes: AnyRoute[] = [dashboardRoute, projectsRoute, projectRoute, staffTodayRoute];
